import { formatDuration, intervalToDuration } from 'date-fns'

import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { DebtPriority } from '../../core/constants/Debts'
import { DebtChartData, sortDebtsByPrioriy } from '../../core/utils/DebtUtils'
import { formatCurrency } from '../../core/utils/NumberFormatting'

type PayoffProjection = {
  payoffSavings: string
  payoffDate: string
  payoffDuration: string
}

export function getPayoffProjection(
  debts: DebtChartData[],
  debtPriority: DebtPriority,
  extraPaydown: number = 0,
): PayoffProjection {
  const sortedDebts = debts
    // Copy debts to avoid overwriting balance during payoff calculation
    .map((d) => ({ ...d }))
    // Filter out invalid debts
    .filter(
      (d) => d.interest_rate !== undefined && d.monthly_payment !== undefined && !d.is_paid_off,
    )
  sortDebtsByPrioriy(sortedDebts, debtPriority)

  let totalSavings = 0
  let monthlyExtra = 0
  const startDate = new Date()
  const endDate = new Date()

  while (sortedDebts.some((debt) => debt.balance >= 0.01 && !debt.is_impossible)) {
    let madePayment = false // Track if any payment was made this month

    for (const [index, debt] of sortedDebts.entries()) {
      if (debt.balance <= 0.01 || debt.is_impossible) continue

      const interestRate = (debt.interest_rate ?? 0) / 100
      const monthlyRate = interestRate / 12
      const monthlyInterest = debt.balance * monthlyRate
      let monthlyPayment = debt.monthly_payment ?? 0

      // Apply extra payment to the highest priority debt
      if (index === 0 && extraPaydown) monthlyPayment += extraPaydown

      // Apply interest before payment
      debt.balance += monthlyInterest
      // Apply monthly payment with any extra payment
      const appliedPayment = Math.min(debt.balance, monthlyPayment + monthlyExtra)

      // If payment doesn't reduce balance, mark as impossible to pay off
      if (appliedPayment <= monthlyInterest) {
        debt.is_impossible = true
        continue
      }

      debt.balance -= appliedPayment
      // Avoid floating-point precision issues
      debt.balance = debt.balance < 0.01 ? 0 : debt.balance
      totalSavings += monthlyInterest

      // If debt was fully paid off, roll over ("snowball") its payment as extra payment
      if (debt.balance <= 0) monthlyExtra += monthlyPayment
      if (appliedPayment > 0) madePayment = true
    }

    // Only increment the month if at least one payment was made
    if (madePayment) endDate.setMonth(endDate.getMonth() + 1)
  }

  // Check if any debts are impossible to pay off
  const hasImpossibleDebts = sortedDebts.some((debt) => debt.is_impossible && debt.balance > 0)
  const duration = intervalToDuration({ start: startDate, end: endDate })

  return {
    payoffSavings: hasImpossibleDebts ? 'N/A' : formatCurrency(totalSavings, '0,0.00'),
    payoffDate: hasImpossibleDebts ? 'Never' : formatDate(endDate, DATE_FORMATS_INTL.MONTH_YEAR),
    payoffDuration: hasImpossibleDebts
      ? 'Stagnant'
      : formatDuration(duration, { format: ['years', 'months'] }) || '0 months',
  }
}
